import { createContext, useContext, useEffect, useId, useRef, useState } from 'react'
import type { CSSProperties, ElementType, FormEvent, MouseEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Alert, Breadcrumbs, Button, Card, Checkbox, Input, ListBox, SearchField, Select, Switch, Tag, TagGroup, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { useUnsaved } from '../lib/unsaved'
import { Icon, type IconName } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import '../styles/settings.css'

// The Settings building blocks on HeroUI (contract: docs/design/settings-redesign.md).

/** A page's heading: optional breadcrumb, the title, one grey line, and the page's actions on the right. */
export function PageHead({
  title,
  line,
  actions,
  crumbs,
  extra,
  mark,
}: {
  title: ReactNode
  line?: ReactNode
  actions?: ReactNode
  /** Earlier steps of the path, each opened by its own handler; the title is the last step. */
  crumbs?: Crumb[]
  /** Beside the title: a tag or a state pill. */
  extra?: ReactNode
  /** Before the title: a queue's stub, a desk's number. */
  mark?: ReactNode
}) {
  const { say } = useStaffText()
  return (
    <header className="set-head">
      {crumbs && crumbs.length > 0 && (
        <Breadcrumbs className="set-head__crumbs" aria-label={say('settings.crumbs')} onAction={(key) => crumbs[Number(key)]?.onOpen()}>
          {crumbs.map((c, i) => (
            <Breadcrumbs.Item key={i} id={String(i)}>
              {c.label}
            </Breadcrumbs.Item>
          ))}
          <Breadcrumbs.Item id="here">{title}</Breadcrumbs.Item>
        </Breadcrumbs>
      )}
      <div className="set-head__title">
        {mark}
        <h1>{title}</h1>
        {extra}
      </div>
      {line && <p className="set-head__line">{line}</p>}
      {actions && <div className="set-head__acts">{actions}</div>}
    </header>
  )
}

/** A string kept as a native tooltip where an explanation used to be drawn. */
export const tipOf = (n: ReactNode): string | undefined => (typeof n === 'string' && n ? n : undefined)

/** A card of rows; an optional title heads it (`line` is its tooltip). */
export function SettingsCard({
  title,
  line,
  status,
  aside,
  children,
  label,
}: {
  title?: ReactNode
  line?: ReactNode
  /** Live data under the title. */
  status?: ReactNode
  aside?: ReactNode
  children: ReactNode
  /** The region's name when there is no title. */
  label?: string
}) {
  return (
    <Card className="set-card" role="region" aria-label={label ?? (typeof title === 'string' ? title : undefined)}>
      {(title || aside) && (
        <header className="set-card__head">
          <span className="set-card__titles">
            {title && (
              <h2 className="set-card__title" title={tipOf(line)}>
                {title}
              </h2>
            )}
            {status && <span className="set-card__line">{status}</span>}
          </span>
          {aside && <span className="set-card__aside">{aside}</span>}
        </header>
      )}
      {children}
    </Card>
  )
}

/**
 * One row of a read view: label on the left (`description` is its tooltip, `status` a
 * visible live line), the value or control on the right. `as="label"` toggles the switch inside.
 */
export function SettingRow({
  label,
  description,
  status,
  tag,
  children,
  control,
  nested,
  disabled,
  as = 'div',
  id,
  onSubmit,
}: {
  label: ReactNode
  description?: ReactNode
  /** Live data under the label: a count, a state. */
  status?: ReactNode
  tag?: ReactNode
  children?: ReactNode
  /** The right side holds one control (switch, picker, button): pushed to the row's end. */
  control?: boolean
  /** Belongs to the row above: indented, no rule between them. */
  nested?: boolean
  disabled?: boolean
  as?: 'div' | 'label' | 'form'
  id?: string
  /** With as="form": the row is a small form (a text and its Save). */
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void
}) {
  const Tag = as as ElementType
  return (
    <Tag
      className="set-row"
      onSubmit={onSubmit}
      data-control={control || undefined}
      data-nested={nested || undefined}
      aria-disabled={disabled || undefined}
    >
      <span className="set-row__text">
        <span className="set-row__label" id={id} title={tipOf(description)}>
          {label}
          {tag}
        </span>
        {status && <span className="set-row__desc">{status}</span>}
      </span>
      {children !== undefined && <span className="set-row__value">{children}</span>}
    </Tag>
  )
}

/** A setting row whose control is one HeroUI Switch; the whole row toggles it. */
export function SwitchRow({
  label,
  description,
  status,
  tag,
  checked,
  disabled,
  dimmed,
  nested,
  onChange,
}: {
  label: string
  description?: ReactNode
  /** Live data under the label: a count, a state. */
  status?: ReactNode
  tag?: ReactNode
  checked: boolean
  disabled?: boolean
  /** Greys the row; `disabled` when not given. */
  dimmed?: boolean
  nested?: boolean
  onChange: (next: boolean) => void
}) {
  const id = useId()
  const grey = dimmed ?? disabled
  return (
    <Switch
      className={'set-row set-row--control' + (nested ? ' set-row--nested' : '')}
      isSelected={checked}
      isDisabled={disabled}
      onChange={onChange}
      aria-labelledby={`${id}-l`}
      aria-describedby={status ? `${id}-d` : undefined}
      data-disabled={grey ? 'true' : undefined}
    >
      {/* Content holds the input: without it the switch draws but cannot be pressed. */}
      <Switch.Content className="set-row__hit">
        <span className="set-row__text">
          <span className="set-row__label" id={`${id}-l`} title={tipOf(description)}>
            {label}
            {tag}
          </span>
          {status && (
            <span className="set-row__desc" id={`${id}-d`}>
              {status}
            </span>
          )}
        </span>
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
      </Switch.Content>
    </Switch>
  )
}

/** A number box with its unit after it ("15 min"). Empty reads as `empty` when given, else 0. */
export function UnitNum({
  value,
  onChange,
  min,
  max,
  unit,
  label,
  disabled,
  empty,
}: {
  value: number | null
  onChange: (next: number | null) => void
  min: number
  max: number
  unit?: string
  label: string
  disabled?: boolean
  /** Shown as the placeholder when the box may be left empty (null). */
  empty?: string
}) {
  return (
    <span className="set-unitnum">
      <Input
        type="number"
        aria-label={label}
        value={value ?? ''}
        min={min}
        max={max}
        placeholder={empty}
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value === '') onChange(empty !== undefined ? null : 0)
          else onChange(Number(e.target.value) || 0)
        }}
      />
      <span className="set-unitnum__unit">{unit}</span>
    </span>
  )
}

/** Label and value pairs inside a row's right side ("Time zone   Asia/Baku"). */
export function Values({ items }: { items: Array<{ label: ReactNode; value: ReactNode; key?: string }> }) {
  return (
    <dl className="set-values">
      {items.map((it, i) => (
        <div key={it.key ?? i} className="set-values__pair">
          <dt>{it.label}</dt>
          <dd>{it.value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** A block under a row inside a card: a list editor, a short form. */
export function SettingBlock({ children }: { children: ReactNode }) {
  return <div className="set-block">{children}</div>
}

/** One option of a ChoiceSelect. */
export interface Choice {
  value: string
  label: string
  disabled?: boolean
}

/** A HeroUI Select over a short list of string values. */
export function ChoiceSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  size,
  className,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  options: readonly Choice[]
  placeholder?: string
  disabled?: boolean
  size?: 'sm'
  className?: string
}) {
  return (
    <Select
      aria-label={label}
      className={'set-select' + (size === 'sm' ? ' set-select--sm' : '') + (className ? ' ' + className : '')}
      value={value === '' ? null : value}
      onChange={(next) => next != null && onChange(String(next))}
      placeholder={placeholder}
      isDisabled={disabled}
      disabledKeys={options.filter((o) => o.disabled).map((o) => o.value)}
    >
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {options.map((o) => (
            <ListBox.Item key={o.value} id={o.value} textValue={o.label}>
              {o.label}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

/** One of a few side by side (HeroUI ToggleButtonGroup, single choice). */
export function Seg<K extends string>({
  label,
  value,
  onChange,
  options,
  disabled,
  className,
}: {
  label: string
  value: K
  onChange: (next: K) => void
  options: ReadonlyArray<{ value: K; label: ReactNode; title?: string; disabled?: boolean }>
  disabled?: boolean
  className?: string
}) {
  return (
    <ToggleButtonGroup
      className={'set-seg' + (className ? ' ' + className : '')}
      aria-label={label}
      size="sm"
      selectionMode="single"
      disallowEmptySelection
      isDisabled={disabled}
      selectedKeys={[value]}
      onSelectionChange={(keys) => {
        const [next] = [...keys]
        if (next !== undefined && next !== value) onChange(String(next) as K)
      }}
    >
      {options.map((o, i) => (
        <ToggleButton key={o.value} id={o.value} aria-label={o.title} isDisabled={o.disabled}>
          {i > 0 && <ToggleButtonGroup.Separator />}
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

/** Short values as HeroUI Tags; with onRemove each has its remove button. */
export function TagList({
  label,
  items,
  onRemove,
  className,
}: {
  label: string
  items: ReadonlyArray<{ id: string; label: ReactNode; text?: string }>
  onRemove?: (id: string) => void
  className?: string
}) {
  if (items.length === 0) return null
  return (
    <TagGroup
      className={'set-tags' + (className ? ' ' + className : '')}
      aria-label={label}
      size="sm"
      onRemove={onRemove && ((keys) => keys.forEach((k) => onRemove(String(k))))}
    >
      <TagGroup.List>
        {items.map((t) => (
          <Tag key={t.id} id={t.id} textValue={t.text ?? (typeof t.label === 'string' ? t.label : t.id)}>
            {t.label}
          </Tag>
        ))}
      </TagGroup.List>
    </TagGroup>
  )
}

/** A tick box with its words after it (HeroUI Checkbox). */
export function Tick({
  checked,
  onChange,
  disabled,
  label,
  className,
  children,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  /** Its name when the words are not enough or there are none. */
  label?: string
  className?: string
  children?: ReactNode
}) {
  return (
    <Checkbox variant="secondary" className={'set-tick' + (className ? ' ' + className : '')} isSelected={checked} isDisabled={disabled} onChange={onChange} aria-label={label}>
      <Checkbox.Content>
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>
        {children}
      </Checkbox.Content>
    </Checkbox>
  )
}

export interface Chip<K extends string> {
  value: K
  label: string
  count?: number
}

/** One row of filter chips beside others in a ListCard. */
export interface ChipGroup {
  label: string
  chips: Chip<string>[]
  chip: string
  onChip: (value: string) => void
}

function Chips<K extends string>({ label, chips, chip, onChip }: { label: string; chips: Chip<K>[]; chip?: K; onChip?: (k: K) => void }) {
  return (
    <ToggleButtonGroup
      className="set-chips"
      aria-label={label}
      size="sm"
      isDetached
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={chip === undefined ? [] : [chip]}
      onSelectionChange={(keys) => {
        const [next] = [...keys]
        if (next !== undefined) onChip?.(String(next) as K)
      }}
    >
      {chips.map((c) => (
        <ToggleButton key={c.value} id={c.value}>
          {c.label}
          {c.count !== undefined && <span className="set-chip__n">{c.count}</span>}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

/** A list page's card: title and grey counts, a full-width search, filter chips, then the table. */
export function ListCard<K extends string = string>({
  title,
  count,
  action,
  search,
  onSearch,
  searchLabel,
  chips,
  chip,
  onChip,
  chipGroups,
  children,
  foot,
}: {
  title: string
  count?: ReactNode
  /** At the right of the title: the list's own add button when the page head has another main action. */
  action?: ReactNode
  search?: string
  onSearch?: (q: string) => void
  searchLabel?: string
  chips?: Chip<K>[]
  chip?: K
  onChip?: (k: K) => void
  /** Several filter rows side by side, in place of `chips`; a group of one chip is left out. */
  chipGroups?: ChipGroup[]
  children: ReactNode
  foot?: ReactNode
}) {
  const groups = chipGroups?.filter((g) => g.chips.length > 1) ?? []
  return (
    <Card className="set-list" role="region" aria-label={title}>
      <header className="set-list__head">
        <div className="set-list__top">
          <h2 className="set-list__title">{title}</h2>
          {count !== undefined && <span className="set-list__count">{count}</span>}
          {action && <span className="set-list__action">{action}</span>}
        </div>
        {onSearch && <Find value={search ?? ''} onChange={onSearch} label={searchLabel} />}
        {chips && chips.length > 1 && <Chips label={title} chips={chips} chip={chip} onChip={onChip} />}
        {groups.length > 0 && (
          <div className="set-chipgroups">
            {groups.map((g) => (
              <Chips key={g.label} label={g.label} chips={g.chips} chip={g.chip} onChip={g.onChip} />
            ))}
          </div>
        )}
      </header>
      {children}
      {foot}
    </Card>
  )
}

/** A HeroUI search box: the list's filter by typing. */
export function Find({
  value,
  onChange,
  onSubmit,
  label,
  className,
}: {
  value: string
  onChange: (q: string) => void
  /** Enter in the box. */
  onSubmit?: () => void
  label?: string
  className?: string
}) {
  return (
    <SearchField className={className} fullWidth={!className} value={value} onChange={onChange} onSubmit={onSubmit} aria-label={label}>
      <SearchField.Group>
        <SearchField.SearchIcon />
        <SearchField.Input placeholder={label} />
        <SearchField.ClearButton />
      </SearchField.Group>
    </SearchField>
  )
}

/** The "add" row at the foot of a list card (its `foot`), for a list whose page head has no room for it. */
export function AddRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div className="set-addrow">
      <Button variant="ghost" size="sm" onPress={onClick}>
        <Icon name="plus" size={16} /> {label}
      </Button>
    </div>
  )
}

/** A table of grid rows inside a ListCard; `cols` is the grid template shared by head and rows. */
export function ListTable({ label, head, cols, children }: { label: string; head: ReactNode[]; cols: string; children: ReactNode }) {
  return (
    <div role="table" aria-label={label} className="set-table" style={{ ['--set-cols' as string]: cols } as CSSProperties}>
      <div className="set-table__head" role="row">
        {head.map((h, i) => (
          <span key={i} role="columnheader">
            {h}
          </span>
        ))}
      </div>
      {children}
    </div>
  )
}

/**
 * One table row: a button when it opens something, else a plain row. With `actions`
 * it opens on click or Enter but may hold its own buttons and switches, which keep their clicks.
 */
export function ListRow({
  onOpen,
  off,
  children,
  label,
  actions,
}: {
  onOpen?: () => void
  off?: boolean
  children: ReactNode
  label?: string
  actions?: boolean
}) {
  if (onOpen && actions) {
    const click = (e: MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest('button,a,input,select,textarea,label')) return
      onOpen()
    }
    return (
      <div
        className="set-trow"
        role="row"
        tabIndex={0}
        aria-label={label}
        data-open
        data-off={off || undefined}
        onClick={click}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target === e.currentTarget) onOpen()
        }}
      >
        {children}
      </div>
    )
  }
  if (onOpen) {
    return (
      <button type="button" className="set-trow" role="row" aria-label={label} data-off={off || undefined} onClick={onOpen}>
        {children}
      </button>
    )
  }
  return (
    <div className="set-trow" role="row" data-off={off || undefined}>
      {children}
    </div>
  )
}

/** The first cell of a row: a tile, a name and a grey line. */
export function NameCell({ tile, name, sub }: { tile?: ReactNode; name: ReactNode; sub?: ReactNode }) {
  return (
    <span className="set-name" role="cell">
      {tile}
      <span className="set-name__text">
        <b>{name}</b>
        {sub && <small>{sub}</small>}
      </span>
    </span>
  )
}

/** A heading row inside a ListTable over the rows that belong together (an operation profile's). */
export function ListGroupRow({ name }: { name: ReactNode }) {
  return (
    <div className="set-tgroup" role="row">
      <span role="cell" className="label-caps">
        {name}
      </span>
    </div>
  )
}

/** The arrow at the end of a row that opens. */
export function RowArrow() {
  return (
    <span className="set-arrow" aria-hidden="true">
      <Icon name="right" size={16} />
    </span>
  )
}

/** A steady tile colour for a record, from its id. */
export const toneOf = (id: string) => ((([...id].reduce((n, c) => n + c.charCodeAt(0), 0) % 6) + 1) as 1 | 2 | 3 | 4 | 5 | 6)

/** An icon or short code on a soft tint; tone picks one of the six tile colours. */
export function IconTile({
  icon,
  text,
  tone = 1,
  size = 'md',
}: {
  icon?: IconName
  text?: string
  tone?: 1 | 2 | 3 | 4 | 5 | 6
  size?: 'sm' | 'md' | 'lg' | 'xl'
}) {
  const px = size === 'xl' ? 28 : size === 'lg' ? 22 : 17
  return (
    <span className="set-tile" data-tone={tone} data-size={size} aria-hidden="true">
      {icon ? <Icon name={icon} size={px} /> : text}
    </span>
  )
}

/** An edit page's body: the form card on the left, summary and danger cards on the right. */
export function EditLayout({ children, side }: { children: ReactNode; side?: ReactNode }) {
  return (
    <div className="set-edit">
      <Card className="set-edit__main">{children}</Card>
      {side && <aside className="set-edit__side">{side}</aside>}
    </div>
  )
}

/** One section of an edit page's form: a round icon and title (`line` is its tooltip), then its fields. */
export function FormSection({
  icon,
  title,
  line,
  status,
  aside,
  children,
  id,
}: {
  icon: IconName
  title: string
  line?: ReactNode
  /** A visible state under the title (an empty section). */
  status?: ReactNode
  aside?: ReactNode
  children: ReactNode
  id?: string
}) {
  return (
    <section className="set-sec" aria-labelledby={id}>
      <header className="set-sec__head">
        <span className="set-sec__icon" aria-hidden="true">
          <Icon name={icon} size={15} />
        </span>
        <span className="set-sec__titles">
          <h2 className="set-sec__title" id={id} title={tipOf(line)}>
            {title}
          </h2>
          {status && <span className="set-sec__line">{status}</span>}
        </span>
        {aside && <span className="set-sec__aside">{aside}</span>}
      </header>
      <div className="set-sec__body">{children}</div>
    </section>
  )
}

/** A field's label with "(optional)" in grey or a required star. */
export function FieldLabel({ text, optional, required }: { text: string; optional?: boolean; required?: boolean }) {
  const { say } = useStaffText()
  return (
    <span className="set-label">
      {text}
      {required && (
        <span className="set-label__req" aria-hidden="true">
          *
        </span>
      )}
      {optional && <span className="set-label__opt">{say('settings.optional')}</span>}
    </span>
  )
}

/** The right column's summary: a band, an icon tile, the name, a line, a state and two or three numbers. */
export function SummaryCard({
  band,
  tile,
  name,
  sub,
  state,
  stats,
}: {
  band: string
  tile: ReactNode
  name: ReactNode
  sub?: ReactNode
  state?: ReactNode
  stats?: Array<{ label: string; value: ReactNode }>
}) {
  return (
    <Card className="set-sum" role="region" aria-label={band}>
      <div className="set-sum__band">{band}</div>
      <div className="set-sum__body">
        {tile}
        <span className="set-sum__name">{name}</span>
        {sub && <span className="set-sum__sub">{sub}</span>}
        {state && <span className="set-sum__state">{state}</span>}
        {stats && stats.length > 0 && (
          <dl className="set-stats" style={{ ['--set-n' as string]: stats.length } as CSSProperties}>
            {stats.map((s) => (
              <div key={s.label}>
                <dd>{s.value}</dd>
                <dt>{s.label}</dt>
              </div>
            ))}
          </dl>
        )}
      </div>
    </Card>
  )
}

/** A destructive action kept apart on a red-edged card: its title (`line` its tooltip), a refusal (`why`), the button. */
export function DangerCard({ title, line, why, action }: { title: string; line?: string; why?: string; action: ReactNode }) {
  return (
    <Card className="set-danger" role="region" aria-label={title}>
      <span className="set-danger__text">
        <b title={line}>{title}</b>
        {why && <span>{why}</span>}
      </span>
      {action}
    </Card>
  )
}

/**
 * Save for a page with a form. Nothing changed: a disabled Save in the page head.
 * Unsaved edits: the save bar at the foot of the window, with Discard beside Save.
 */
export function SaveActions({
  dirty,
  busy,
  blocked,
  onDiscard,
  onSave,
  saveLabel,
  count,
}: {
  dirty: boolean
  busy?: boolean
  /** Holds back Save only (the draft has a problem); Discard stays. */
  blocked?: boolean
  onDiscard?: () => void
  onSave: () => void
  saveLabel?: string
  /** How many changes the draft holds, when the page counts them. */
  count?: number
}) {
  const { say } = useStaffText()
  const save = (
    <Button variant="primary" isDisabled={!dirty || busy || blocked} onPress={onSave}>
      <Icon name="check" size={16} /> {saveLabel ?? say('settings.saveChanges')}
    </Button>
  )
  if (!dirty) return save
  return createPortal(
    <Card className="set-savebar" role="region" aria-label={say('ui.unsaved')}>
      <span className="set-savebar__n">{count !== undefined ? say('settings.changes', { n: count }) : say('ui.unsaved')}</span>
      {onDiscard && (
        <Button variant="ghost" isDisabled={busy} onPress={onDiscard}>
          {say('ui.discard')}
        </Button>
      )}
      {save}
    </Card>,
    document.body,
  )
}

/** "Discard your changes?" asked in place, with Keep editing and Discard. */
export function DiscardAsk({ label, onKeep, onDiscard }: { label: string; onKeep: () => void; onDiscard: () => void }) {
  const { say } = useStaffText()
  return (
    <Alert status="warning" className="set-ask" role="alertdialog" aria-label={label}>
      <Alert.Content className="set-ask__text">
        <Alert.Title>{say('ui.discardAsk')}</Alert.Title>
      </Alert.Content>
      <Button variant="ghost" size="sm" autoFocus onPress={onKeep}>
        {say('ui.keepEditing')}
      </Button>
      <Button variant="danger" size="sm" onPress={onDiscard}>
        {say('ui.discard')}
      </Button>
    </Alert>
  )
}

/** A refusal or a note, said on the page until the next try. */
export function Notice({ children, tone = 'danger', className }: { children: ReactNode; tone?: 'danger' | 'warning' | 'accent' | 'default'; className?: string }) {
  return (
    <Alert status={tone} className={className} role={tone === 'danger' ? 'alert' : undefined}>
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Description>{children}</Alert.Description>
      </Alert.Content>
    </Alert>
  )
}

/** One earlier step of a page's path. */
export interface Crumb {
  label: string
  onOpen: () => void
}

/**
 * Where a section is below its list: the steps after `#section` in the address
 * ([] on the list, ['new'], [id], [id, 'queue', qid]). Opening pushes, so Back returns.
 */
export interface SubPageNav {
  /** This level's own address (#section/…), '' outside Settings. */
  here: string
  path: string[]
  /** Pushes these steps below this level. */
  open: (...steps: string[]) => void
  /** Returns to these steps below this level, by Back when the open entry was pushed from there. */
  back: (steps: string[]) => void
  /** Puts these steps in place of the open entry: a record just made replaces its New page. */
  swap: (...steps: string[]) => void
}

const SubPageContext = createContext<SubPageNav | null>(null)
export const SubPageProvider = SubPageContext.Provider

// Outside Settings (a page of its own, a test) a section keeps its steps in memory.
function useLocalNav(): SubPageNav {
  const [path, setPath] = useState<string[]>([])
  return { here: '', path, open: (...steps) => setPath(steps), back: (steps) => setPath(steps), swap: (...steps) => setPath(steps) }
}

/** The address below this section (or below the nearest SubPageScope); `code` is the next step. */
export function useSubPage(): SubPageNav & { code: string; close: () => void } {
  const local = useLocalNav()
  const nav = useContext(SubPageContext) ?? local
  return { ...nav, code: nav.path[0] ?? '', close: () => nav.back([]) }
}

/** The step of a record being added. */
export const NEW = 'new'
/** The step of a section's one settings form (mail server, gateway). */
export const EDIT = 'edit'

/**
 * The record a list's page is open on, from the address: `isNew`, a row found by its id,
 * or none. An id the list does not hold, once read, returns to the list (deleted, or a stale link).
 */
export function useOpenRecord<T>(
  rows: readonly T[],
  idOf: (row: T) => string,
  ready: boolean,
  /** Steps that name no row: a second kind of new record, a settings page of the section. */
  pages: readonly string[] = [],
) {
  const nav = useSubPage()
  const { code } = nav
  const named = code === NEW || pages.includes(code)
  const row = code && !named ? rows.find((r) => idOf(r) === code) : undefined
  const missing = ready && code !== '' && !named && !row
  const closeRef = useRef(nav.close)
  closeRef.current = nav.close
  useEffect(() => {
    if (missing) closeRef.current()
  }, [missing])
  return {
    code,
    row,
    isNew: code === NEW,
    open: (r?: T) => nav.open(r ? idOf(r) : NEW),
    close: nav.close,
    nav,
  }
}

/** Gives the children the address below one open step: a page inside a page. */
export function SubPageScope({ at, children }: { at: string; children: ReactNode }) {
  const parent = useContext(SubPageContext)
  if (!parent) return <>{children}</>
  const nav: SubPageNav = {
    here: parent.here && `${parent.here}/${encodeURIComponent(at)}`,
    path: parent.path[0] === at ? parent.path.slice(1) : [],
    open: (...steps) => parent.open(at, ...steps),
    back: (steps) => parent.back([at, ...steps]),
    swap: (...steps) => parent.swap(at, ...steps),
  }
  return <SubPageProvider value={nav}>{children}</SubPageProvider>
}

/** The HeroUI button variant an InnerPage's submit takes from its old class name. */
function submitVariant(cls: string): 'primary' | 'danger' | 'secondary' {
  if (cls.includes('danger')) return 'danger'
  return cls.includes('primary') || cls.includes('publish') ? 'primary' : 'secondary'
}

/**
 * A record's own page inside Settings, drawn in place of the list it was opened from:
 * the path back, the title, Cancel and Save in the head, the form in a card and a
 * destructive action at its foot. Leaving with unsaved typing asks first, Back included.
 */
export function InnerPage({
  back,
  before,
  title,
  mark,
  tag,
  line,
  onClose,
  onSubmit,
  submitLabel,
  valid = true,
  busy = false,
  error,
  extra,
  closeLabel,
  hideSubmit = false,
  wide = false,
  asForm = true,
  submitClass = 'btn--primary',
  busyLabel,
  actions,
  dirty: edited = false,
  bare = false,
  children,
}: {
  /** The list this page was opened from: the last crumb, which closes the page. Left out when `before` ends the path. */
  back?: string
  /** Steps before `back` on a page inside a page. */
  before?: Crumb[]
  title: string
  mark?: ReactNode
  /** Beside the title: a state pill. */
  tag?: ReactNode
  line?: ReactNode
  onClose: () => void
  onSubmit?: () => void
  submitLabel?: string
  valid?: boolean
  busy?: boolean
  /** What the server refused, said above the form. */
  error?: string
  /** The form's foot: a destructive action, or a short note. */
  extra?: ReactNode
  closeLabel?: string
  hideSubmit?: boolean
  /** The page's full width, for a form holding a table or a tree. */
  wide?: boolean
  /** Off for a form holding controls this file did not write, where a <button> with no type would submit. */
  asForm?: boolean
  submitClass?: string
  busyLabel?: string
  /** More buttons in the head, before Cancel. */
  actions?: ReactNode
  /** Edits made by controls that fire no input event (the drawn Select, chips); typing counts by itself. */
  dirty?: boolean
  /** The form draws its own cards (EditLayout): no card around it. */
  bare?: boolean
  children: ReactNode
}) {
  const { say } = useStaffText()
  const formId = useId()
  const top = useRef<HTMLElement>(null)
  const [typed, setTyped] = useState(false)
  const dirty = typed || edited
  const [asking, setAsking] = useState<(() => void) | null>(null)
  const wasBusy = useRef(busy)
  // The address this page lives at: a move to a page inside it keeps its edits.
  const [scope] = useState(() => window.location.hash || undefined)
  useUnsaved(dirty, scope)

  // A save that went through: what was typed is now what is stored. A refusal is
  // said at the top of the form, which may be scrolled away from.
  useEffect(() => {
    if (wasBusy.current && !busy) {
      if (!error) setTyped(false)
      top.current?.querySelector('.set-inner__card :is([role=alert], .notice)')?.scrollIntoView?.({ block: 'nearest' })
    }
    wasBusy.current = busy
  }, [busy, error])

  useEffect(() => {
    top.current?.scrollIntoView?.({ block: 'start' })
    top.current
      ?.querySelector<HTMLElement>('.set-inner__body input:not([type=checkbox]):not([type=radio]):not(:disabled), .set-inner__body textarea:not(:disabled)')
      ?.focus({ preventScroll: true })
  }, [])

  useEffect(() => {
    if (!dirty) return
    const ask = (e: BeforeUnloadEvent) => e.preventDefault()
    addEventListener('beforeunload', ask)
    return () => removeEventListener('beforeunload', ask)
  }, [dirty])

  const leave = (go: () => void) => (dirty ? setAsking(() => go) : go())
  const submit = !hideSubmit && onSubmit && submitLabel
  const Frame = asForm ? 'form' : 'div'
  const crumbs = [...(before ?? []), ...(back ? [{ label: back, onOpen: onClose }] : [])].map((c) => ({ label: c.label, onOpen: () => leave(c.onOpen) }))

  return (
    <section className="set-inner" ref={top} aria-label={title} data-wide={wide || undefined}>
      <PageHead
        crumbs={crumbs}
        title={title}
        mark={mark}
        extra={tag}
        line={line}
        actions={
          <>
            {actions}
            <Button variant="ghost" onPress={() => leave(onClose)}>
              {closeLabel ?? (submit ? say('common.cancel') : say('ui.close'))}
            </Button>
            {submit && (
              <Button
                type={asForm ? 'submit' : 'button'}
                form={asForm ? formId : undefined}
                variant={submitVariant(submitClass)}
                isDisabled={!valid || busy}
                onPress={asForm ? undefined : () => valid && !busy && onSubmit?.()}
              >
                <Icon name="check" size={16} /> {busy ? (busyLabel ?? say('common.saving')) : submitLabel}
              </Button>
            )}
          </>
        }
      />
      {asking && (
        <DiscardAsk
          label={say('ui.unsaved')}
          onKeep={() => setAsking(null)}
          onDiscard={() => {
            const go = asking
            setAsking(null)
            setTyped(false)
            go()
          }}
        />
      )}
      <Card
        render={(props) => (
          <Frame
            {...(props as object)}
            id={asForm ? formId : undefined}
            onSubmit={(e: FormEvent) => {
              e.preventDefault()
              e.stopPropagation()
              if (valid && !busy && onSubmit) onSubmit()
            }}
            onInput={() => setTyped(true)}
            onChange={() => setTyped(true)}
          />
        )}
        className="set-inner__card"
        data-bare={bare || undefined}
      >
        {error && <Notice>{error}</Notice>}
        <div className="set-inner__body">{children}</div>
        {extra && <footer className="set-inner__foot">{extra}</footer>}
      </Card>
    </section>
  )
}
